import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import { ReentryBriefResult, ScreenerQueueFilter, ScreenerQueueHttpService } from '@tsin-core/services/http/screener-queue.service';
import { SaveAdminStep } from '@tsin-core/states/applications/applications.actions';
import { catchError, tap } from 'rxjs';
import {
  AssignScreener,
  AutoDistribute,
  ClearScreenerQueue,
  DismissReentryBrief,
  FinalizeDecision,
  InitiateQCReview,
  LoadApplicationForProcessing,
  LoadCDReviewQueue,
  LoadManagerReviewQueue,
  LoadQCApplication,
  LoadQCQueue,
  LoadReentryBrief,
  LoadScreenerQueue,
  LoadScreenerQueueApplication,
  LoadScreenerStats,
  PullFromDraw,
  SetScreenerQueueFilter,
  SubmitQCReview,
  SubmitScreeningDecision,
  TriggerDocumentValidation,
  TriggerKyeForDraw,
  TriggerResumeValidation,
  UnassignScreener,
  UpdateBriefDecision,
  UpdateQueueStatus,
} from './screener-queue.actions';

export class ScreenerQueueStateModel {
  queueItems: any[] = [];
  total: number = 0;
  page: number = 1;
  limit: number = 25;
  filter: ScreenerQueueFilter = {};
  selectedApplication: any = null;
  stats: any[] = [];
  qcQueue: any[] = [];
  cdReviewQueue: any[] = [];
  /** Phase 3A — Applications with preliminary decisions awaiting manager sign-off.
   *  Candidatures avec décisions préliminaires en attente de validation du gestionnaire. */
  managerReviewQueue: any[] = [];
  selectedQCApplication: any = null;
  loading: LoadingState = LoadingState.notLoading;
  /** B4 — Re-entry brief for the currently open application / Résumé de reprise */
  reentryBrief: ReentryBriefResult | null = null;
  /** Whether the re-entry brief banner is dismissed by the user / Bannière masquée */
  reentryBriefDismissed: boolean = false;
  /** B5 — Document validation state (Pass/Flag/Fail) for the open application / État de validation des documents */
  documentValidationLoading: boolean = false;
  /** B6 — Resume validation state (Pass/Flag/Fail) for the open application / État de conformité du CV */
  resumeValidationLoading: boolean = false;
  /** Application loaded for the screener processing view / Candidature chargée pour la vue de traitement */
  processingApplication: any = null;
  /** Whether a screening decision submission is in progress / Soumission de décision en cours */
  decisionSubmitting: boolean = false;
  /** Phase 1D — Whether a finalize-decision call is in progress / Finalisation de décision en cours */
  finalizingDecision: boolean = false;
  /** Result of the last pull-from-draw operation — used to display the correct activated count.
   *  Résultat du dernier tirage activé — utilisé pour afficher le nombre correct de candidatures activées. */
  lastPullResult: { activated: number; drawName: string } | null = null;
  /** Result of the last auto-distribute operation — used to display the correct assigned count in the success toast.
   *  Résultat de la dernière distribution automatique — utilisé pour afficher le nombre correct dans le message de succès. */
  lastAutoDistributeResult: { assigned: number; total: number } | null = null;
}

const defaults: ScreenerQueueStateModel = {
  queueItems: [],
  total: 0,
  page: 1,
  limit: 25,
  filter: {},
  selectedApplication: null,
  stats: [],
  qcQueue: [],
  cdReviewQueue: [],
  managerReviewQueue: [],
  selectedQCApplication: null,
  loading: LoadingState.notLoading,
  reentryBrief: null,
  reentryBriefDismissed: false,
  documentValidationLoading: false,
  resumeValidationLoading: false,
  processingApplication: null,
  decisionSubmitting: false,
  finalizingDecision: false,
  lastPullResult: null,
  lastAutoDistributeResult: null,
};

@State<ScreenerQueueStateModel>({ name: 'screenerQueueState', defaults })
@Injectable()
export class ScreenerQueueState {
  private readonly svc = inject(ScreenerQueueHttpService);

  @Selector() static getQueueItems(s: ScreenerQueueStateModel) {
    return s.queueItems;
  }
  @Selector() static getTotal(s: ScreenerQueueStateModel) {
    return s.total;
  }
  @Selector() static getFilter(s: ScreenerQueueStateModel) {
    return s.filter;
  }
  @Selector() static getSelectedApplication(s: ScreenerQueueStateModel) {
    return s.selectedApplication;
  }
  @Selector() static getStats(s: ScreenerQueueStateModel) {
    return s.stats;
  }
  @Selector() static getQCQueue(s: ScreenerQueueStateModel) {
    return s.qcQueue;
  }
  @Selector() static getCDReviewQueue(s: ScreenerQueueStateModel) {
    return s.cdReviewQueue;
  }
  /** Selector for Phase 3A manager review queue / Sélecteur pour la file du gestionnaire (Phase 3A) */
  @Selector() static getManagerReviewQueue(s: ScreenerQueueStateModel) {
    return s.managerReviewQueue;
  }
  @Selector() static getSelectedQCApplication(s: ScreenerQueueStateModel) {
    return s.selectedQCApplication;
  }
  @Selector() static getLoading(s: ScreenerQueueStateModel) {
    return s.loading;
  }
  @Selector() static getReentryBrief(s: ScreenerQueueStateModel) {
    return s.reentryBrief;
  }
  @Selector() static getReentryBriefDismissed(s: ScreenerQueueStateModel) {
    return s.reentryBriefDismissed;
  }
  /** Application loaded for screener processing / Candidature pour le traitement */
  @Selector() static getProcessingApplication(s: ScreenerQueueStateModel) {
    return s.processingApplication;
  }
  /** Whether a screening decision is being submitted / Soumission en cours */
  @Selector() static getDecisionSubmitting(s: ScreenerQueueStateModel) {
    return s.decisionSubmitting;
  }
  /** Phase 1D — Whether a finalize-decision call is in progress / Finalisation de décision en cours */
  @Selector() static getFinalizingDecision(s: ScreenerQueueStateModel) {
    return s.finalizingDecision;
  }

  @Action(SetScreenerQueueFilter)
  setFilter({ patchState }: StateContext<ScreenerQueueStateModel>, { filter }: SetScreenerQueueFilter) {
    patchState({ filter });
  }

  @Action(ClearScreenerQueue)
  clear({ setState }: StateContext<ScreenerQueueStateModel>) {
    setState(defaults);
  }

  @Action(LoadScreenerQueue)
  loadQueue({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { filter }: LoadScreenerQueue) {
    patchState({ loading: LoadingState.loadingList });
    const merged = { ...getState().filter, ...filter };
    return this.svc.getQueue(merged).pipe(
      tap(({ data, total, page, limit }) => patchState({ queueItems: data, total, page, limit, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(LoadScreenerQueueApplication)
  loadApplication({ patchState }: StateContext<ScreenerQueueStateModel>, { id }: LoadScreenerQueueApplication) {
    patchState({ loading: LoadingState.loadingList });
    return this.svc.getApplication(id).pipe(
      tap((selectedApplication) => patchState({ selectedApplication, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(LoadScreenerStats)
  loadStats({ patchState }: StateContext<ScreenerQueueStateModel>, { cohortId }: LoadScreenerStats) {
    return this.svc.getStats(cohortId).pipe(tap((stats) => patchState({ stats })));
  }

  @Action(AssignScreener)
  assignScreener({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: AssignScreener) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.assignScreener(id, payload).pipe(
      tap((updated) => {
        const queueItems = getState().queueItems.map((a) => (a._id === id ? updated : a));
        patchState({ queueItems, selectedApplication: updated, loading: LoadingState.notLoading });
      }),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(UnassignScreener)
  unassignScreener({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: UnassignScreener) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.unassignScreener(id, payload).pipe(
      tap((updated) => {
        const queueItems = getState().queueItems.map((a) => (a._id === id ? updated : a));
        patchState({ queueItems, selectedApplication: updated, loading: LoadingState.notLoading });
      }),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(UpdateQueueStatus)
  updateStatus({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: UpdateQueueStatus) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.updateQueueStatus(id, payload).pipe(
      tap((updated) => {
        const queueItems = getState().queueItems.map((a) => (a._id === id ? updated : a));
        patchState({ queueItems, selectedApplication: updated, loading: LoadingState.notLoading });
      }),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(AutoDistribute)
  autoDistribute({ patchState }: StateContext<ScreenerQueueStateModel>, { payload }: AutoDistribute) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.autoDistribute(payload).pipe(
      tap((res) => patchState({ lastAutoDistributeResult: res, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(LoadQCQueue)
  loadQCQueue({ patchState }: StateContext<ScreenerQueueStateModel>, { filter }: LoadQCQueue) {
    patchState({ loading: LoadingState.loadingList });
    return this.svc.getQCQueue(filter).pipe(
      tap((qcQueue) => patchState({ qcQueue, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(LoadCDReviewQueue)
  loadCDReviewQueue({ patchState }: StateContext<ScreenerQueueStateModel>, { cohortId }: LoadCDReviewQueue) {
    patchState({ loading: LoadingState.loadingList });
    return this.svc.getCDReviewQueue({ cohortId }).pipe(
      tap((cdReviewQueue) => patchState({ cdReviewQueue, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  /** Phase 3A — Load applications with preliminary decisions for manager sign-off.
   *  Phase 3A — Charger les candidatures avec décisions préliminaires pour validation du gestionnaire. */
  @Action(LoadManagerReviewQueue)
  loadManagerReviewQueue({ patchState }: StateContext<ScreenerQueueStateModel>, { filter }: LoadManagerReviewQueue) {
    patchState({ loading: LoadingState.loadingList });
    return this.svc.getManagerReviewQueue(filter).pipe(
      tap((managerReviewQueue) => patchState({ managerReviewQueue, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(LoadQCApplication)
  loadQCApplication({ patchState }: StateContext<ScreenerQueueStateModel>, { id }: LoadQCApplication) {
    patchState({ loading: LoadingState.loadingList });
    return this.svc.getQCApplication(id).pipe(
      tap((selectedQCApplication) => patchState({ selectedQCApplication, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(InitiateQCReview)
  initiateQCReview({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id }: InitiateQCReview) {
    return this.svc.initiateQCReview(id).pipe(
      tap((updated) => {
        const qcQueue = getState().qcQueue.map((a) => (a._id === id ? updated : a));
        patchState({ qcQueue, selectedQCApplication: updated });
      }),
    );
  }

  @Action(SubmitQCReview)
  submitQCReview({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: SubmitQCReview) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.submitQCReview(id, payload).pipe(
      tap((updated) => {
        const qcQueue = getState().qcQueue.map((a) => (a._id === id ? updated : a));
        patchState({ qcQueue, selectedQCApplication: updated, loading: LoadingState.notLoading });
      }),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  @Action(UpdateBriefDecision)
  updateBriefDecision({ patchState, getState }: StateContext<ScreenerQueueStateModel>, { id, payload }: UpdateBriefDecision) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.svc.updateBriefDecision(id, payload).pipe(
      tap((updated) => {
        const cdReviewQueue = getState().cdReviewQueue.map((a) => (a._id === id ? updated : a));
        patchState({ cdReviewQueue, selectedQCApplication: updated, loading: LoadingState.notLoading });
      }),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  // ─── B4: AI Screening Brief Actions ────────────────────────────────────────

  /** Fetch (or generate) the re-entry brief for a given application.
   *  Récupère (ou génère) le résumé de reprise pour une candidature donnée. */
  @Action(LoadReentryBrief)
  loadReentryBrief({ patchState }: StateContext<ScreenerQueueStateModel>, { applicationId }: LoadReentryBrief) {
    patchState({ reentryBrief: null, reentryBriefDismissed: false, loading: LoadingState.loadingList });
    return this.svc.getReentryBrief(applicationId).pipe(
      tap((brief) => patchState({ reentryBrief: brief, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        // Non-fatal: brief load failure should not break the application view
        // Non fatal : l'échec de chargement du résumé ne doit pas bloquer la vue
        console.warn('[ScreenerQueueState] Re-entry brief load failed:', e);
        return [];
      }),
    );
  }

  /** Mark the re-entry brief banner as dismissed (session-local).
   *  Marquer la bannière du résumé de reprise comme masquée. */
  @Action(DismissReentryBrief)
  dismissReentryBrief({ patchState }: StateContext<ScreenerQueueStateModel>) {
    patchState({ reentryBriefDismissed: true });
  }

  // ─── B5: AI Document Validation ────────────────────────────────────────────

  @Selector()
  static getDocumentValidationLoading(s: ScreenerQueueStateModel) {
    return s.documentValidationLoading;
  }

  /** Trigger document validation; refreshes the selected application on completion.
   *  Déclencher la validation des documents; rafraîchit la candidature sélectionnée à la fin. */
  @Action(TriggerDocumentValidation)
  triggerDocumentValidation({ patchState, dispatch }: StateContext<ScreenerQueueStateModel>, { id }: TriggerDocumentValidation) {
    patchState({ documentValidationLoading: true });
    return this.svc.triggerDocumentValidation(id).pipe(
      tap(() => {
        patchState({ documentValidationLoading: false });
        dispatch(new LoadScreenerQueueApplication(id));
      }),
      catchError((e) => {
        patchState({ documentValidationLoading: false });
        console.warn('[ScreenerQueueState] Document validation failed:', e);
        throw e;
      }),
    );
  }

  // ─── B6: Resume Template Compliance ────────────────────────────────────────

  @Selector()
  static getResumeValidationLoading(s: ScreenerQueueStateModel) {
    return s.resumeValidationLoading;
  }

  /** Trigger resume validation; refreshes the selected application on completion.
   *  Déclencher la validation du CV; rafraîchit la candidature sélectionnée à la fin. */
  @Action(TriggerResumeValidation)
  triggerResumeValidation({ patchState, dispatch }: StateContext<ScreenerQueueStateModel>, { id }: TriggerResumeValidation) {
    patchState({ resumeValidationLoading: true });
    return this.svc.triggerResumeValidation(id).pipe(
      tap(() => {
        patchState({ resumeValidationLoading: false });
        dispatch(new LoadScreenerQueueApplication(id));
      }),
      catchError((e) => {
        patchState({ resumeValidationLoading: false });
        console.warn('[ScreenerQueueState] Resume validation failed:', e);
        throw e;
      }),
    );
  }

  // ─── Screening Workflow ────────────────────────────────────────────────────

  /** Load a single application for the screener processing detail view.
   *  Charger une candidature pour la vue de traitement de l'examinateur. */
  @Action(LoadApplicationForProcessing)
  loadApplicationForProcessing({ patchState }: StateContext<ScreenerQueueStateModel>, { id }: LoadApplicationForProcessing) {
    patchState({ loading: LoadingState.loadingList, processingApplication: null });
    return this.svc.getApplication(id).pipe(
      tap((processingApplication) => patchState({ processingApplication, loading: LoadingState.notLoading })),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading });
        throw e;
      }),
    );
  }

  /** Pull applications from a draw into the screener queue (status = Pending).
   *  Activer les candidatures d'un tirage dans la file d'attente (statut = Pending). */
  @Action(PullFromDraw)
  pullFromDraw({ patchState }: StateContext<ScreenerQueueStateModel>, { payload }: PullFromDraw) {
    return this.svc.pullFromDraw(payload).pipe(
      tap((result) => patchState({ lastPullResult: { activated: result.activated, drawName: result.drawName } })),
      catchError((e) => {
        throw e;
      }),
    );
  }

  /** Optimistically sync admin step data into the processing application so the
   *  step view immediately reflects saved notes without a round-trip reload.
   *  Synchronisation optimiste des données admin dans la candidature en traitement. */
  @Action(SaveAdminStep)
  syncAdminStepToProcessingApp({ getState, patchState }: StateContext<ScreenerQueueStateModel>, { payload }: SaveAdminStep) {
    const { processingApplication } = getState();
    if (!processingApplication) return;
    const steps = (processingApplication.steps ?? []).map((s: any) => (s.name === payload.step ? { ...s, adminData: payload.data } : s));
    patchState({ processingApplication: { ...processingApplication, steps } });
  }

  /** Manually (re-)trigger KYE agents for all applications in an activated draw.
   *  fire-and-forget: async event emission on the server — no state change needed.
   *  Déclencher manuellement les agents KYE pour toutes les candidatures d'un tirage activé. */
  @Action(TriggerKyeForDraw)
  triggerKyeForDraw(_: StateContext<ScreenerQueueStateModel>, { drawId, force, retrospective }: TriggerKyeForDraw) {
    return this.svc.triggerKyeForDraw(drawId, force, retrospective);
  }

  /** Submit a preliminary screening decision and route the application.
   *  Soumettre une décision préliminaire et acheminer la candidature. */
  @Action(SubmitScreeningDecision)
  submitScreeningDecision({ patchState }: StateContext<ScreenerQueueStateModel>, { id, payload }: SubmitScreeningDecision) {
    patchState({ decisionSubmitting: true });
    return this.svc.submitScreeningDecision(id, payload).pipe(
      tap(() => patchState({ decisionSubmitting: false })),
      catchError((e) => {
        patchState({ decisionSubmitting: false });
        throw e;
      }),
    );
  }

  /** Phase 1D — Finalize the most recent preliminary decision and refresh the processing view.
   *  Phase 1D — Finaliser la décision préliminaire la plus récente et rafraîchir la vue de traitement. */
  @Action(FinalizeDecision)
  finalizeDecision({ patchState, dispatch }: StateContext<ScreenerQueueStateModel>, { id, payload }: FinalizeDecision) {
    patchState({ finalizingDecision: true });
    return this.svc.finalizeDecision(id, payload).pipe(
      tap((updated) => {
        patchState({ finalizingDecision: false, processingApplication: updated });
        // Refresh the manager review queue so finalized apps disappear from it
        dispatch(new LoadManagerReviewQueue());
      }),
      catchError((e) => {
        patchState({ finalizingDecision: false });
        throw e;
      }),
    );
  }
}
